import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { removeAppsFromStartup, removeFromStartup, startupQuery } from '../api/devices'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import { SAS_CSS } from './DeviceStartupServices'
import type { ScreenProps } from './screens'

/**
 * Fila Startup Apps - opteemum/devices/details/startupApps.php.
 *
 * Remove (un rand) posteaza la `/devices/{id}/startup/remove`
 * (Opteemum::removeFromStartup), Auto remove (bifele "Recommended Removal")
 * la `/devices/{id}/startup/remove-many` (Opteemum::removeAppsFromStartup).
 * Aceleasi intrebari si aceleasi texte ca scriptul machetei; randurile
 * scoase dispar din lista, ca acolo. Din tichet, comanda STD poarta numarul
 * tichetului.
 */

export function DeviceStartupApps({ id, ticket }: ScreenProps) {
  const { data, error } = useQuery(startupQuery(id))
  const queryClient = useQueryClient()
  const [picked, setPicked] = useState<Record<number, boolean>>({})
  const [busy, setBusy] = useState(false)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const isPicked = (app: { id: number; recommended: boolean }) => picked[app.id] ?? app.recommended

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['device', id, 'startup'] })

  const removeOne = async (app: { id: number; name: string }) => {
    if (busy || !window.confirm('Are you sure?')) return
    setBusy(true)
    try {
      await removeFromStartup(id, app.id, ticket ?? 0)
      toast('Instruction to remove application from startup was sent', 'Success')
      await refresh()
    } catch {
      toast('Something went wrong!', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const autoRemove = async () => {
    const apps = data.applications.filter((app) => app.is_removable && isPicked(app)).map((app) => app.id)
    if (apps.length === 0 || busy) return
    if (!window.confirm('Are you sure?\n\nAll checked applications will be removed!')) return
    setBusy(true)
    try {
      await removeAppsFromStartup(id, apps, ticket ?? 0)
      toast('Command sent.', 'Success')
      setPicked({})
      await refresh()
    } catch {
      toast('An error occured!Please try again', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <style>{SAS_CSS}</style>
      <div className="sas-section">
        <p className="sas-hint">
          Session autostart (Run keys, Startup folder, XDG .desktop) — not boot services.
          {data.is_linux && <> Auto remove is not available on Linux yet.</>}
        </p>

        <table className="table table-striped table-hover" width="100%">
          <thead>
            <tr>
              <th className="a-left">Application</th>
              <th className="a-left">User</th>
              <th className="a-left">Location</th>
              <th className="a-left">Command</th>
              <th className="a-left">Impact</th>
              <th className="a-left">Action</th>
              <th className="a-left">Recommended Removal</th>
            </tr>
          </thead>
          <tbody>
            {data.applications.length > 0 ? (
              data.applications.map((app) => (
                <tr key={app.id} id={`row_app_${app.id}`}>
                  <td className="td-small">
                    <strong>{app.name}</strong>
                  </td>
                  <td className="td-small">{app.user}</td>
                  <td className="td-small">{app.location}</td>
                  <td className="td-small">{app.command}</td>
                  <td className="td-small">{app.impact !== '' ? app.impact : <em>n/a</em>}</td>
                  <td className="td-small">
                    {app.is_removable && (
                      <button className="btn btn-sm btn-danger" type="button" disabled={busy} onClick={() => void removeOne(app)}>
                        <i className="fas fa-times"></i> Remove
                      </button>
                    )}
                  </td>
                  <td className="td-small">
                    {app.is_removable && (
                      <center>
                        <SettingsCheck
                          aria-label={`Recommended removal - ${app.name}`}
                          className="remove-apps"
                          value={app.id}
                          checked={isPicked(app)}
                          onChange={(on) => setPicked((was) => ({ ...was, [app.id]: on }))}
                        />
                      </center>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>
                  <center>No data available</center>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!data.is_linux && (
          <button type="button" className="btn btn-danger auto-remove-apps" disabled={busy} onClick={() => void autoRemove()}>
            Auto remove
          </button>
        )}
      </div>
    </>
  )
}
